import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
} from "material-react-table";

import { useEffect, useMemo, useState, useCallback } from "react";
import OverlayLoader from "../../../components/OverlayLoader";
import { setSelectedRule } from "../../../redux/distributionRuleSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { getAmountType } from "../../../utils";
import { getCommissionRulesList } from "../../../redux/SystemManager/commissionRules";
import debounce from "lodash/debounce";
import { isDefaultClause } from "typescript";
import { AmountOrPercentCell } from "../../../components/Common/currencyColumn";
import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
  emptyRowsFallback: {
    // The cell spans every column, so on a table wider than its container the
    // text would centre on the full scroll width (off screen). Pin it to the
    // visible area instead; its width is set to the container's at render.
    position: "sticky",
    left: 0,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Vertically centred in the table body, as before.
    minHeight: "calc(100vh - 350px)",
    textAlign: "center",
    whiteSpace: "normal",
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    color: "#f00",
  },
});

// ---------------------------
// CASE TYPE MAPPING FUNCTION
// ---------------------------
const mapCaseType = (key: string) => {
  const mapping: any = {
    investment: ["Unit-linked", "Asset (Investment)"],
    morgages: ["Mortgages"],
    "loans-lease-hp": ["Loans"],
    "Life Assurance": ["Life Assurance"],
    Pensions: ["Pensions"],
    Investments: ["Investments"],
    "Savings Plans": ["Savings Plans"],
    "Income Protection": ["Income Protection"],
    "Health Assurance": ["Health Assurance"],
    General: ["General"],
  };

  return mapping[key] || [key];
};

// ---------------------------
// BUILD API QUERY STRING
// ---------------------------
export const buildCaseTypeQuery = (key: string | undefined) => {
  if (!key) return "";
  // Client fees have no case, so only rules without a case type apply.
  if (key === "fees-charges") return "null";

  const mapped = mapCaseType(key);
  const mappedStr = mapped.join(",");

  // API requires: type1,type2,null
  return `${mappedStr},null`;
};

const DistributionRule = (props: any) => {
  const styles = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const { list, loading, count } = useSelector(
    (state: RootState) => state.commissionRules
  );

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [partyName, setPartyName] = useState("");

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  // -------------------------------------------
  // DEBOUNCED API FETCH
  // -------------------------------------------
  const debouncedFetchRules = useCallback(
    debounce((params: any) => {
      dispatch(getCommissionRulesList(params));
    }, 350),
    []
  );

  // -------------------------------------------
  // MAIN API FETCH (Runs on case_type, search, pagination)
  // -------------------------------------------
  const fetchRules = useCallback(() => {
    const caseTypeQuery = buildCaseTypeQuery(props?.options?.case_type);
    const params = {
      isDefault: "",
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      partyName: partyName || "",
      caseType: caseTypeQuery, // API FILTERING
    };

    debouncedFetchRules(params);
  }, [pagination, partyName, props?.options?.case_type]);

  // trigger fetch
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // -------------------------------------------
  // TABLE COLUMNS
  // -------------------------------------------
  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return [
      { accessorKey: "party_type", header: "Party Type" },
      {
        accessorKey: "case_type",
        header: "Case Type",
        Cell: ({ row }: any) =>
          row.original.case_type === "Unit-linked"
            ? "Asset (Investment)"
            : row.original.case_type,
      },
      { accessorKey: "party_name", header: "Party Name" },
      {
        accessorKey: "amount",
        header: "Amount",
        // A percentage is never money — it keeps its raw value and % suffix.
        Cell: ({ row }: any) => (
          <AmountOrPercentCell
            value={row.original.amount}
            amountFlag={row.original.amount_flag}
          />
        ),
      },
      {
        accessorKey: "amount_flag",
        header: "Amount Type",
        Cell: ({ row }: any) => getAmountType(row.original.amount_flag),
      },
      { accessorKey: "note_text", header: "Note" },
    ];
  }, []);

  // -------------------------------------------
  // ROW SELECTION
  // -------------------------------------------
  useEffect(() => {
    const selectedId = Object.keys(rowSelection)[0];
    if (!selectedId) return;

    const rule = list.find((x: any) => x.id === selectedId);
    if (rule) {
      dispatch(setSelectedRule(rule));
      props.ruleId(rule);
    }
  }, [rowSelection, list]);

  // -------------------------------------------
  // PRESELECT ROW WHEN Editing
  // -------------------------------------------
  useEffect(() => {
    if (props.options && props.options.getRuleId && list.length > 0) {
      let index = 0;
      const ruleId =
        props.options.getRuleId["commission_rule_id"] ??
        props.options.getRuleId["id"];

      index = list.findIndex((x: any) => x.id === ruleId);

      if (index !== -1) {
        const id = list[index].id;
        setRowSelection({ [id]: true });
        dispatch(setSelectedRule(list[index]));
        props.ruleId(list[index]);
      }
    }
  }, [props.options, list]);

  // -------------------------------------------
  // SEARCH HANDLER
  // -------------------------------------------
  const handleSearch = useCallback((val: string) => {
    setPartyName(val);
  }, []);

  return (
    <>
      <OverlayLoader isLoading={loading} />

      <div className="client-list-wrapper">
        <MaterialReactTable
          columns={columns}
          data={list}
          getRowId={(row: any) => row.id}
          state={{
            rowSelection,
            pagination,
            globalFilter: partyName,
          }}
          enableStickyHeader
          positionToolbarAlertBanner={"none"}
          enableRowSelection
          enableSelectAll={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableHiding={false}
          enableDensityToggle={false}
          enableRowActions={false}
          enableMultiRowSelection={false}
          enableGlobalFilter={true}
          onGlobalFilterChange={handleSearch}
          onRowSelectionChange={setRowSelection}
          manualPagination
          enableFullScreenToggle={false}
          rowCount={count}
          onPaginationChange={setPagination}
          muiTableContainerProps={({ table }) => ({
            sx: {
              height: table.getState().isFullScreen
                ? "calc(100vh - 90px) !important"
                : "calc(100vh - 290px) !important",
            },
          })}
          renderEmptyRowsFallback={({ table }) => (
            <div
              className={styles.emptyRowsFallback}
              // Same sizing MRT's default fallback uses: the visible width of
              // the table's container, never the scrollable table width.
              style={{
                width:
                  table.refs.tableContainerRef.current?.clientWidth ?? "100%",
              }}
            >
              {partyName
                ? "No matching records found for the entered search criteria"
                : "No records to display"}
            </div>
          )}
        />
      </div>
    </>
  );
};

export default DistributionRule;
